import { stateOptions } from '@brands/Utils/selectOptions';

import { getStateFromGeocode } from './getStateFromGeocode';

const normalizeName = (value?: string | null): string => (value || '').trim().toLowerCase();

// AWS Location reports the country as ISO 3166-1 alpha-3 ("USA"), but accept
// the common alternates too — reading a patient who is actually in the US as
// foreign is worse than the loose match this replaces.
const UNITED_STATES_COUNTRIES = ['usa', 'us', 'united states', 'united states of america'];

// Match the geocoded region name exactly — never as a substring. The endpoint
// behind this reverse-geocodes any coordinates on earth and returns the
// first-order subdivision, so a loose match reads foreign regions as US
// states: "Jersey" (Channel Islands) is a substring of "New Jersey" and
// "Mexico" of "New Mexico". Those resolved to NJ and NM and were persisted as
// a confirmed location, so the patient was never prompted to correct them.
// AWS returns full state names for US locations ("New York"), and
// abbreviations and the "NY - New York" label form are matched below, so
// requiring an exact match costs nothing for patients who are actually in the US.
export const getStateCodeFromGeocodeState = (stateName?: string | null): string | null => {
  const normalizedStateName = normalizeName(stateName);
  if (!normalizedStateName) return null;

  const exactMatch = stateOptions.find((option) => {
    const [abbreviation, name] = option.label.split(' - ');
    return [option.value, abbreviation, name, option.label].some(
      (value) => normalizeName(value) === normalizedStateName,
    );
  });

  return exactMatch?.value || null;
};

// Resolve a geocode response to a US state, rejecting anything known to be
// outside the country. This is the check that makes the region name safe to
// trust: exact matching alone still can't tell a US state from a foreign
// region that happens to share its name.
//
// A missing country means the response predates the API returning one — fall
// through to name matching rather than dropping detection for every patient
// until that deploys.
export const getStateCodeFromGeocodeResponse = (response?: {
  state?: string | null;
  country?: string | null;
}): string | null => {
  const country = normalizeName(response?.country);
  if (country && !UNITED_STATES_COUNTRIES.includes(country)) return null;

  return getStateCodeFromGeocodeState(response?.state);
};

// getCurrentPosition's own `timeout` runs from call time and, per spec, includes
// however long the user takes to respond to the permission prompt — commonly
// several seconds. The reverse-geocode request rides the shared API client,
// which has no per-request timeout, so it needs its own budget that starts
// once a position is actually in hand — racing it against a clock that started
// before the permission prompt even appeared left it almost no time to
// complete, which resolved to null and triggered the "no US location" fallback
// for users whose location was in fact successfully resolved.
const GEOCODE_LOOKUP_TIMEOUT_MS = 8000;

export const getBrowserLocationState = async (): Promise<string | null> => {
  if (typeof navigator === 'undefined' || !navigator.geolocation) return null;

  const position = await new Promise<GeolocationPosition | null>((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve(pos),
      () => resolve(null),
      // State-level accuracy is all we need. High accuracy waits on GPS, which
      // desktops lack and phones can stall on indoors — it makes the lookup time
      // out and return null, which is why the location silently fell back to a
      // stale cached selection. Network positioning is fast and sufficient here.
      { timeout: 10000, enableHighAccuracy: false },
    );
  });

  if (!position) return null;

  const geocode = (async (): Promise<string | null> => {
    try {
      const { latitude: lat, longitude: lng } = position.coords;
      const response = await getStateFromGeocode(lat, lng);
      return getStateCodeFromGeocodeResponse(response);
    } catch {
      return null;
    }
  })();

  const timeout = new Promise<string | null>((resolve) => {
    setTimeout(() => resolve(null), GEOCODE_LOOKUP_TIMEOUT_MS);
  });

  return Promise.race([geocode, timeout]);
};
